import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const ODISHA_CITIES = [
  "All of Odisha", "Bhubaneswar", "Rourkela", "Bondamunda", "Cuttack", "Berhampur", "Puri", "Sambalpur",
  "Angul", "Balangir", "Balasore (Baleshwar)", "Bargarh", "Bhadrak", "Boudh",
  "Deogarh (Debagarh)", "Dhenkanal", "Gajapati", "Ganjam", "Jagatsinghpur", "Jajpur",
  "Jharsuguda", "Kalahandi", "Kandhamal", "Kendrapara", "Keonjhar (Kendujhar)", "Khordha",
  "Koraput", "Malkangiri", "Mayurbhanj", "Nabarangpur", "Nayagarh", "Nuapada", "Rayagada",
  "Subarnapur (Sonepur)", "Sundargarh", "Outside Odisha",
];

/**
 * EditProfileDialog — self-service profile editing for the logged-in runner.
 * Receives `user` (the current profile being viewed) and `onUpdate(updated)` callback.
 * Trigger is passed as `children`.
 */
export default function EditProfileDialog({ user, children, onUpdate }) {
  const { setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: user.name || "",
    bio: user.bio || "",
    city: user.city || "",
    profession: user.profession || "",
    height_cm: user.height_cm ?? "",
    weight_kg: user.weight_kg ?? "",
    running_history: user.running_history || "",
    future_goals: user.future_goals || "",
  });

  const save = async () => {
    if (!f.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/auth/profile", {
        name: f.name.trim(),
        bio: f.bio,
        city: f.city,
        profession: f.profession,
        height_cm: f.height_cm === "" ? null : Number(f.height_cm),
        weight_kg: f.weight_kg === "" ? null : Number(f.weight_kg),
        running_history: f.running_history,
        future_goals: f.future_goals,
      });
      setUser(data);
      onUpdate?.(data);
      toast.success("Profile updated");
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        data-testid="edit-profile-dialog"
        className="bg-zinc-950 border-zinc-800 text-white rounded-none max-w-xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <p className="font-mono text-[10px] tracking-[0.3em] text-orange-500 uppercase">
            YOUR PROFILE
          </p>
          <DialogTitle className="font-display text-3xl text-white tracking-wide">
            Edit your details
          </DialogTitle>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <Field label="FULL NAME">
            <Input
              data-testid="edit-name"
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              className="rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600"
            />
          </Field>

          <Field label="BIO">
            <Textarea
              data-testid="edit-bio"
              value={f.bio}
              onChange={(e) => setF({ ...f, bio: e.target.value })}
              placeholder="A line or two about your running journey…"
              className="rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600 min-h-[80px]"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="CITY">
              <Select value={f.city} onValueChange={(v) => setF({ ...f, city: v })}>
                <SelectTrigger data-testid="edit-city" className="rounded-none bg-zinc-900 border-zinc-800 text-white focus:ring-orange-600">
                  <SelectValue placeholder="Pick your city" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none max-h-72">
                  {ODISHA_CITIES.map((c) => (
                    <SelectItem key={c} value={c} className="rounded-none focus:bg-orange-600 focus:text-white">{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="PROFESSION / WORK">
              <Input
                data-testid="edit-profession"
                value={f.profession}
                onChange={(e) => setF({ ...f, profession: e.target.value })}
                placeholder="e.g., Steel engineer, doctor…"
                className="rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="HEIGHT (CM)">
              <Input
                data-testid="edit-height"
                type="number"
                value={f.height_cm}
                onChange={(e) => setF({ ...f, height_cm: e.target.value })}
                placeholder="172"
                className="rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600"
              />
            </Field>
            <Field label="WEIGHT (KG)">
              <Input
                data-testid="edit-weight"
                type="number"
                step="0.1"
                value={f.weight_kg}
                onChange={(e) => setF({ ...f, weight_kg: e.target.value })}
                placeholder="68"
                className="rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600"
              />
            </Field>
          </div>

          <Field label="RUNNING HISTORY">
            <Textarea
              data-testid="edit-history"
              value={f.running_history}
              onChange={(e) => setF({ ...f, running_history: e.target.value })}
              placeholder="Races, distances, PBs…"
              className="rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600 min-h-[80px]"
            />
          </Field>

          <Field label="FUTURE GOALS">
            <Textarea
              data-testid="edit-goals"
              value={f.future_goals}
              onChange={(e) => setF({ ...f, future_goals: e.target.value })}
              placeholder="First half-marathon? Sub-4 marathon? An ultra?"
              className="rounded-none bg-zinc-900 border-zinc-800 text-white focus-visible:ring-orange-600 min-h-[80px]"
            />
          </Field>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2 pt-4 border-t border-zinc-800">
          <Button
            variant="outline"
            onClick={() => setOpen(false)}
            data-testid="edit-cancel-btn"
            className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-900 font-display tracking-wider"
          >
            CANCEL
          </Button>
          <Button
            onClick={save}
            disabled={busy}
            data-testid="edit-save-btn"
            className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider px-6"
          >
            {busy ? "SAVING…" : "SAVE CHANGES"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="font-display tracking-widest text-xs text-zinc-400">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
